import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { authFileUrl } from "@/lib/fileurl";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Store, Upload, Save, Image as ImageIcon, FileText } from "lucide-react";

const empty = { brand_name: "", phone: "", address: "", email: "", logo_url: "", receipt_footer: "" };
const emptyFiscal = { rtn_emisor: "", razon_social: "", cai: "", establecimiento: "000", punto_emision: "001", tipo_documento: "01", rango_inicial: "", rango_final: "", fecha_limite_emision: "" };

export default function Ajustes() {
  const { refreshContext } = useAuth();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fiscal, setFiscal] = useState(emptyFiscal);
  const [fiscalInfo, setFiscalInfo] = useState({});
  const [savingFiscal, setSavingFiscal] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/my/branding").then((r) => setForm({ ...empty, ...r.data })).catch(() => {}); loadFiscal(); }, []);

  const loadFiscal = () => api.get("/my/fiscal-config").then((r) => { setFiscal({ ...emptyFiscal, ...r.data }); setFiscalInfo(r.data); }).catch(() => {});

  const saveFiscal = async (e) => {
    e.preventDefault(); setSavingFiscal(true);
    try {
      await api.put("/my/fiscal-config", {
        rtn_emisor: fiscal.rtn_emisor, razon_social: fiscal.razon_social, cai: fiscal.cai,
        establecimiento: fiscal.establecimiento, punto_emision: fiscal.punto_emision, tipo_documento: fiscal.tipo_documento,
        rango_inicial: parseInt(fiscal.rango_inicial || 0, 10), rango_final: parseInt(fiscal.rango_final || 0, 10),
        fecha_limite_emision: fiscal.fecha_limite_emision || null,
      });
      toast.success("Configuración fiscal guardada"); loadFiscal();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingFiscal(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("La imagen supera 3 MB.");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: r.data.url }));
      toast.success("Logo cargado. No olvides guardar.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/my/branding", form);
      toast.success("Datos del gimnasio guardados");
      if (refreshContext) refreshContext();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const preview = form.logo_url ? authFileUrl(form.logo_url) : "";

  return (
    <div data-testid="ajustes-page">
      <div className="mb-8"><p className="overline text-[#00f0ff]">Personalización</p><h1 className="font-display text-5xl">DATOS DEL GIMNASIO</h1></div>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2"><Store className="w-4 h-4 text-[#00f0ff]" /><p className="overline">Información comercial</p></div>
          <div><label className="overline block mb-1.5">Nombre comercial</label>
            <input data-testid="ajustes-brand-name" value={form.brand_name || ""} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="inp" placeholder="Ej. GLG Fitness Center" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="overline block mb-1.5">Teléfono</label>
              <input data-testid="ajustes-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" /></div>
            <div><label className="overline block mb-1.5">Email</label>
              <input data-testid="ajustes-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" /></div>
          </div>
          <div><label className="overline block mb-1.5">Dirección</label>
            <input data-testid="ajustes-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp" /></div>
          <div><label className="overline block mb-1.5">Pie de recibo</label>
            <input data-testid="ajustes-footer" value={form.receipt_footer || ""} onChange={(e) => setForm({ ...form, receipt_footer: e.target.value })} className="inp" placeholder="Gracias por su preferencia." /></div>
          <button type="submit" disabled={busy} data-testid="ajustes-save-button" className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#00c2cc] disabled:opacity-50">
            <Save className="w-4 h-4" /> {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4"><ImageIcon className="w-4 h-4 text-[#00f0ff]" /><p className="overline">Logo</p></div>
          <div className="aspect-square bg-[#121212] border border-dashed border-white/15 flex items-center justify-center mb-4 overflow-hidden">
            {preview ? <img src={preview} alt="logo" className="max-h-full max-w-full object-contain" data-testid="ajustes-logo-preview" />
              : <span className="text-white/30 text-sm">Sin logo</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="ajustes-logo-input" />
          <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="ajustes-logo-upload" className="w-full flex items-center justify-center gap-2 border border-white/20 py-2.5 text-sm hover:bg-white/5 disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : "Subir logo"}
          </button>
          <p className="text-[11px] text-white/40 mt-2">PNG, JPG o WEBP · máx. 3 MB. Aparecerá en los recibos.</p>
        </div>
      </form>

      <form onSubmit={saveFiscal} className="mt-6 bg-[#0a0a0a] border border-white/10 p-6" data-testid="ajustes-fiscal-form">
        <div className="flex items-center gap-2 mb-4"><FileText className="w-4 h-4 text-[#00f0ff]" /><p className="overline">Facturación fiscal (CAI · Honduras)</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="overline block mb-1.5">RTN Emisor</label>
            <input data-testid="fiscal-rtn-emisor" value={fiscal.rtn_emisor || ""} onChange={(e) => setFiscal({ ...fiscal, rtn_emisor: e.target.value })} className="inp" /></div>
          <div><label className="overline block mb-1.5">Razón social</label>
            <input data-testid="fiscal-razon-social" value={fiscal.razon_social || ""} onChange={(e) => setFiscal({ ...fiscal, razon_social: e.target.value })} className="inp" /></div>
        </div>
        <div className="mt-4"><label className="overline block mb-1.5">CAI</label>
          <input data-testid="fiscal-cai" value={fiscal.cai || ""} onChange={(e) => setFiscal({ ...fiscal, cai: e.target.value })} className="inp" placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX" /></div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div><label className="overline block mb-1.5">Establecimiento</label>
            <input data-testid="fiscal-establecimiento" value={fiscal.establecimiento || ""} onChange={(e) => setFiscal({ ...fiscal, establecimiento: e.target.value })} className="inp" placeholder="000" /></div>
          <div><label className="overline block mb-1.5">Punto emisión</label>
            <input data-testid="fiscal-punto-emision" value={fiscal.punto_emision || ""} onChange={(e) => setFiscal({ ...fiscal, punto_emision: e.target.value })} className="inp" placeholder="001" /></div>
          <div><label className="overline block mb-1.5">Tipo doc</label>
            <input data-testid="fiscal-tipo-documento" value={fiscal.tipo_documento || ""} onChange={(e) => setFiscal({ ...fiscal, tipo_documento: e.target.value })} className="inp" placeholder="01" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div><label className="overline block mb-1.5">Rango inicial</label>
            <input data-testid="fiscal-rango-inicial" type="number" value={fiscal.rango_inicial ?? ""} onChange={(e) => setFiscal({ ...fiscal, rango_inicial: e.target.value })} className="inp" placeholder="1" /></div>
          <div><label className="overline block mb-1.5">Rango final</label>
            <input data-testid="fiscal-rango-final" type="number" value={fiscal.rango_final ?? ""} onChange={(e) => setFiscal({ ...fiscal, rango_final: e.target.value })} className="inp" placeholder="500" /></div>
          <div><label className="overline block mb-1.5">Fecha límite emisión</label>
            <input data-testid="fiscal-fecha-limite" type="date" value={(fiscal.fecha_limite_emision || "").slice(0, 10)} onChange={(e) => setFiscal({ ...fiscal, fecha_limite_emision: e.target.value })} className="inp" /></div>
        </div>
        {fiscalInfo?.rango_final ? (
          <p className="text-[11px] text-white/50 mt-3" data-testid="fiscal-correlativo-info">Próximo correlativo: <b className="text-[#00f0ff]">{fiscalInfo.proximo_correlativo}</b> · Facturas restantes en el rango: <b className={fiscalInfo.restantes <= 10 ? "text-[#FF3B30]" : "text-[#39FF14]"}>{fiscalInfo.restantes}</b></p>
        ) : (
          <p className="text-[11px] text-white/40 mt-3">Configura el rango autorizado para emitir facturas con CAI.</p>
        )}
        <button type="submit" disabled={savingFiscal} data-testid="fiscal-save-button" className="mt-4 flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#00c2cc] disabled:opacity-50">
          <Save className="w-4 h-4" /> {savingFiscal ? "Guardando..." : "Guardar facturación"}
        </button>
      </form>
    </div>
  );
}
